/*
 * R.S.P. landing grid (collection-pages-2026). Loaded only by snippets/cp-main-collection.liquid; assets/collections.js
 * is left untouched for the other templates. Fixes AUDIT.md §1 defects:
 *   1 back/forward re-renders from the URL (popstate)
 *   2 pushed URLs keep ?view= and preview_theme_id; empty params, type= and the default sort are dropped
 *   4 one AbortController: a newer request cancels the one in flight
 *   5 dropdown open/close is delegated from this element, so it survives every re-render
 *   6 one breakpoint, 768px, shared with assets/cp-collection.css
 *   7 sheet listeners are bound once; the mobile sheet previews changes and only applies on "Apply · N results"
 * "N results" (bar and Apply) is the in-stock total for the current filters (R09a); the server-rendered card count is
 * the fallback. R13: when the in-stock results fit on one page, it is the number of cards (colour cards included).
 */
(() => {
  if (customElements.get('cp-collection')) return;

  const MQ = window.matchMedia('(min-width: 768px)');
  const KEEP = ['view', 'preview_theme_id'];
  const REGIONS = ['facets', 'apply-count', 'count', 'clear', 'active', 'active-n', 'grid', 'more'];
  const SHEET_REGIONS = ['facets', 'apply-count'];
  // R09b infinite scroll: auto-load when the load-more button comes within 600px of the viewport.
  // Cap rule: in-stock total > 4 pages (PAGE * 4) → auto-load at most AUTO_PAGES per page load, then show the button;
  // a click resumes for AUTO_PAGES more. Total ≤ 4 pages → auto-load to the end, no button shown.
  const PAGE = 48;
  const AUTO_PAGES = 3;

  class CpCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.controller = null;
      this.snapshot = null;
      this.priceTimer = null;
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onPop = this.onPop.bind(this);
      this.onMq = this.onMq.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('toggle', this.onToggle, true);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKey);
      window.addEventListener('popstate', this.onPop);
      MQ.addEventListener('change', this.onMq);
      history.replaceState({ cp: true }, '', location.href);
      this.sizeSort();
      this.previewLinks();
      this.total = null;          // in-stock product total: drives the infinite-scroll cap
      this.shown = null;          // the number "N results" shows (R13 one-page rule)
      this.totalSeq = 0;
      this.inFlight = 0;          // filter / sort / popstate requests in progress: never auto-load during these
      this.loadingMore = false;
      this.autoBudget = null;     // null until the in-stock total is known; Infinity = uncapped
      this.capped = true;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (entries.some((en) => en.isIntersecting)) this.maybeAutoLoad();
        }, { rootMargin: '600px 0px' });
      }
      this.observeMore();
      this.refreshTotal(this.cleanParams(location.search), this.pagesOf(this));
    }

    /* ---------- infinite scroll (R09b) ---------- */

    applyRule() {
      // Unknown total (count fetch failed): behave as capped, so the page never loads without limit.
      this.capped = this.total == null || this.total > PAGE * 4;
      this.autoBudget = this.capped ? AUTO_PAGES : Infinity;
      this.updateMoreUi();
      this.maybeAutoLoad();
    }

    observeMore() {
      if (!this.io) return;
      this.io.disconnect();
      const btn = this.querySelector('[data-cp-more]');
      if (btn) this.io.observe(btn);
      this.updateMoreUi();
    }

    updateMoreUi() {
      const region = this.querySelector('[data-cp-region="more"]');
      if (!region) return;
      const auto = !!this.io && this.autoBudget != null && this.autoBudget > 0;
      region.classList.toggle('cp-more--auto', auto);  // button stays in the DOM; hidden only while auto-loading
    }

    nearViewport(el) {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight + 600 && r.bottom > -600;
    }

    maybeAutoLoad() {
      if (!this.io || this.autoBudget == null || this.autoBudget <= 0 || this.inFlight > 0 || this.loadingMore) return;
      const btn = this.querySelector('[data-cp-more]');
      if (!btn || btn.disabled || !this.nearViewport(btn)) return;
      this.autoBudget -= 1;
      this.loadMore(btn);
    }

    /* ---------- result count (R09a, D-R09-1; one-page rule R13) ----------
     * "N results" = the collection's in-stock total for the current filters, not the cards on this page. One extra
     * Section Rendering fetch with filter.v.availability=1; the section exposes collection.products_count as data-total.
     * R13: if that in-stock response has a single page (data-pages = 1) AND the visible listing does too, every card is
     * on this page, so the count is its card count (data-cards: colour-variant cards included). Otherwise the in-stock
     * product total. R35: the visible listing includes sold-out products (skipped when rendered), so it can page while the
     * in-stock response fits on one page (CA flannel-shirts: 51 products, 48 in stock) — that case is the product total.
     * visiblePages: data-pages of the listing for these params (null / NaN when unknown → product total).
     * If the fetch fails, the server-rendered count stays (fallback). URL, default filters and the sold-out skip unchanged.
     * Returns { total, shown } or null. */
    async fetchTotal(params, visiblePages = null) {
      const p = this.cleanParams(new URLSearchParams(params).toString());
      const av = p.getAll('filter.v.availability');
      if (av.length && !av.includes('1')) return { total: 0, shown: 0 };  // only "out of stock" selected: nothing in stock
      p.delete('filter.v.availability');
      p.append('filter.v.availability', '1');
      const cur = new URLSearchParams(location.search);
      KEEP.forEach((k) => { if (cur.has(k)) p.set(k, cur.get(k)); });
      p.set('section_id', this.sectionId);
      try {
        const res = await fetch(`${location.pathname}?${p}`, { credentials: 'same-origin' });
        if (!res.ok) return null;
        const el = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('cp-collection');
        const n = el ? parseInt(el.dataset.total, 10) : NaN;
        if (!Number.isFinite(n)) return null;
        const pages = parseInt(el.dataset.pages, 10);
        const cards = parseInt(el.dataset.cards, 10);
        const onePage = pages === 1 && visiblePages === 1;
        return { total: n, shown: onePage && Number.isFinite(cards) ? cards : n };
      } catch (e) {
        return null;
      }
    }

    fmt(one, other, n) { return (n === 1 ? one : other).replace(/\d+/, n); }

    showTotal(n) {
      const el = this.querySelector('[data-cp-region="count"]');
      if (el) el.textContent = this.fmt(el.dataset.one, el.dataset.other, n);
    }

    showApplyTotal(n) {
      const el = this.querySelector('[data-cp-region="apply-count"]');
      if (el && this.dataset.applyOne) el.textContent = this.fmt(this.dataset.applyOne, this.dataset.applyOther, n);
    }

    pagesOf(el) { return el ? parseInt(el.dataset.pages, 10) : NaN; }

    async refreshTotal(params, visiblePages = null) {
      const seq = ++this.totalSeq;
      const r = await this.fetchTotal(params, visiblePages);
      if (seq !== this.totalSeq) return;  // a newer request won
      this.total = r ? r.total : null;
      this.shown = r ? r.shown : null;
      if (r) {  // else fallback: keep the server-rendered count
        this.showTotal(r.shown);
        this.showApplyTotal(r.shown);
      }
      this.applyRule();
    }

    async previewTotal(params, visiblePages = null) {
      const seq = ++this.totalSeq;
      const r = await this.fetchTotal(params, visiblePages);
      if (seq === this.totalSeq && r) this.showApplyTotal(r.shown);
    }

    // Size the sort pill to its selected option (the open list can still be long). Measures with a hidden span.
    sizeSort() {
      const sel = this.querySelector('[data-cp-sort]');
      if (!sel || !sel.selectedOptions[0]) return;
      const probe = document.createElement('span');
      const cs = getComputedStyle(sel);
      probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:${cs.font};letter-spacing:${cs.letterSpacing}`;
      probe.textContent = sel.selectedOptions[0].textContent;
      document.body.appendChild(probe);
      const w = probe.getBoundingClientRect().width + parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + 2;
      probe.remove();
      sel.style.width = `${Math.min(Math.ceil(w), 220)}px`;
    }

    // Preview convenience only: while testing with ?view=rsp-landing, keep it on the circle-row links. Guarded on the
    // actual request parameter; at cut-over (no ?view=) the links are untouched.
    previewLinks() {
      const view = new URLSearchParams(location.search).get('view');
      if (view !== 'rsp-landing') return;
      document.querySelectorAll('.cp-circle__link').forEach((a) => {
        const u = new URL(a.href, location.origin);
        u.searchParams.set('view', view);
        a.href = u.pathname + u.search;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('popstate', this.onPop);
      MQ.removeEventListener('change', this.onMq);
    }

    get form() { return this.querySelector('form[data-cp-form]'); }
    get sheet() { return this.querySelector('[data-cp-sheet]'); }
    get sheetOpen() { return !MQ.matches && this.sheet.classList.contains('is-open'); }

    /* ---------- params ---------- */

    formParams() {
      const p = new URLSearchParams();
      for (const [k, v] of new FormData(this.form)) {
        if (v === '' || k === 'type' || k === 'page') continue;
        if (k === 'sort_by' && v === this.dataset.defaultSort) continue;
        p.append(k, v);
      }
      return p;
    }

    cleanParams(search) {
      const p = new URLSearchParams(search);
      for (const k of [...p.keys()]) {
        const vals = p.getAll(k).filter((v) => v !== '');
        p.delete(k);
        if (KEEP.includes(k) || k === 'page' || k === 'section_id' || k === 'type') continue;
        if (k === 'sort_by' && vals[0] === this.dataset.defaultSort) continue;
        vals.forEach((v) => p.append(k, v));
      }
      return p;
    }

    pageUrl(params) {
      const p = new URLSearchParams(params);
      const cur = new URLSearchParams(location.search);
      KEEP.forEach((k) => { if (cur.has(k)) p.set(k, cur.get(k)); });
      const qs = p.toString();
      return location.pathname + (qs ? `?${qs}` : '');
    }

    /* ---------- fetch + swap ---------- */

    async fetchDoc(params, page, { quiet = false } = {}) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const p = new URLSearchParams(params);
      const cur = new URLSearchParams(location.search);
      KEEP.forEach((k) => { if (cur.has(k)) p.set(k, cur.get(k)); });
      if (page) p.set('page', page);
      p.set('section_id', this.sectionId);
      if (!quiet) this.classList.add('is-loading');
      try {
        const res = await fetch(`${location.pathname}?${p}`, { signal: this.controller.signal, credentials: 'same-origin' });
        const html = await res.text();
        return new DOMParser().parseFromString(html, 'text/html').querySelector('cp-collection');
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
        return null;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    swap(doc, names) {
      const open = [...this.querySelectorAll('.cp-facet[open]')].map((d) => d.dataset.cpFacet);
      const focusId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;
      names.forEach((name) => {
        const from = doc.querySelector(`[data-cp-region="${name}"]`);
        const to = this.querySelector(`[data-cp-region="${name}"]`);
        if (from && to) to.innerHTML = from.innerHTML;
      });
      open.forEach((key) => {
        const d = this.querySelector(`.cp-facet[data-cp-facet="${CSS.escape(key)}"]`);
        if (d) d.open = true;
      });
      if (focusId) {
        const el = document.getElementById(focusId);
        if (el) el.focus({ preventScroll: true });
      }
      this.afterRender();
    }

    // After load-more: keep the in-stock total if we have it; otherwise fall back to the cards in the grid (D1).
    updateCount() {
      if (this.shown != null) { this.showTotal(this.shown); return; }
      const el = this.querySelector('[data-cp-region="count"]');
      if (!el) return;
      // R12: colour-variant products render several cards; count distinct products (data-cp-product).
      const n = new Set([...this.querySelectorAll('[data-cp-region="grid"] [data-cp-product]')].map((e) => e.dataset.cpProduct)).size;
      el.textContent = (n === 1 ? el.dataset.one : el.dataset.other).replace(/\d+/, n);
    }

    afterRender() {
      if (window.theme && theme.mlcurrency && typeof window.currenciesChange === 'function') {
        window.currenciesChange(this.querySelectorAll('.cp-card span.money'));
      }
    }

    async render(params, { push = true } = {}) {
      this.inFlight += 1;
      this.autoBudget = null;  // rule is recomputed once the new in-stock total arrives (refreshTotal → applyRule)
      let doc;
      try { doc = await this.fetchDoc(params); } finally { this.inFlight -= 1; }
      if (!doc) return;
      this.swap(doc, REGIONS);
      this.observeMore();
      const sort = this.querySelector('[data-cp-sort]');
      const docSort = doc.querySelector('[data-cp-sort]');
      if (sort && docSort) sort.value = docSort.value;
      this.sizeSort();
      const url = this.pageUrl(params);
      if (push) history.pushState({ cp: true }, '', url);
      this.refreshTotal(params, this.pagesOf(doc));
    }

    async preview(params) {
      this.inFlight += 1;
      let doc;
      try { doc = await this.fetchDoc(params); } finally { this.inFlight -= 1; }
      if (!doc) return;
      this.swap(doc, SHEET_REGIONS);
      this.previewTotal(params, this.pagesOf(doc));
    }

    async loadMore(btn) {
      if (this.loadingMore || this.inFlight > 0) return;
      this.loadingMore = true;
      btn.disabled = true;
      let doc;
      try { doc = await this.fetchDoc(this.cleanParams(location.search), btn.dataset.page, { quiet: true }); } finally { this.loadingMore = false; }
      if (!doc) { btn.disabled = false; return; }
      const grid = this.querySelector('[data-cp-region="grid"]');
      const more = doc.querySelector('[data-cp-region="grid"]');
      if (grid && more) grid.insertAdjacentHTML('beforeend', more.innerHTML);
      const region = this.querySelector('[data-cp-region="more"]');
      const next = doc.querySelector('[data-cp-region="more"]');
      if (region) region.innerHTML = next ? next.innerHTML : '';
      this.updateCount();
      this.afterRender();
      this.observeMore();
      requestAnimationFrame(() => this.maybeAutoLoad());  // still near the bottom → keep going while budget lasts
    }

    /* ---------- events ---------- */

    onChange(e) {
      const t = e.target;
      if (!(t.form === this.form || this.form.contains(t))) return;
      if (t.matches('[data-cp-price]')) {
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.changed(), 600);
        return;
      }
      this.changed();
    }

    changed() {
      if (this.sheetOpen) this.preview(this.formParams());
      else this.render(this.formParams());
    }

    onSubmit(e) {
      if (e.target !== this.form) return;
      e.preventDefault();
      clearTimeout(this.priceTimer);
      this.snapshot = null;
      this.closeSheet();
      this.render(this.formParams());
    }

    onClick(e) {
      const t = e.target.closest('[data-cp-open],[data-cp-close],[data-cp-clear],[data-cp-link],[data-cp-more]');
      if (!t || !this.contains(t)) return;
      if (t.matches('[data-cp-open]')) { e.preventDefault(); this.openSheet(); return; }
      if (t.matches('[data-cp-close]')) { e.preventDefault(); this.closeSheet(true); return; }
      if (t.matches('[data-cp-more]')) {
        e.preventDefault();
        if (this.capped && (this.autoBudget == null || this.autoBudget <= 0)) this.autoBudget = AUTO_PAGES;  // click resumes for 3 more
        this.updateMoreUi();
        this.loadMore(t);
        return;
      }
      if (t.matches('[data-cp-clear]')) {
        e.preventDefault();
        this.form.querySelectorAll('input[type=checkbox]').forEach((i) => { i.checked = false; });
        this.form.querySelectorAll('[data-cp-price]').forEach((i) => { i.value = ''; });
        this.changed();
        return;
      }
      if (t.matches('[data-cp-link]')) {
        e.preventDefault();
        this.render(this.cleanParams(new URL(t.href, location.origin).search));
      }
    }

    onToggle(e) {
      const d = e.target;
      if (!MQ.matches || !d.matches || !d.matches('.cp-facet') || !d.open) return;
      this.querySelectorAll('.cp-facet[open]').forEach((o) => { if (o !== d) o.open = false; });
    }

    onDocClick(e) {
      if (!MQ.matches) return;
      if (e.target.closest && e.target.closest('.cp-facet')) return;
      this.querySelectorAll('.cp-facet[open]').forEach((o) => { o.open = false; });
    }

    onKey(e) {
      if (e.key !== 'Escape') return;
      if (this.sheetOpen) { this.closeSheet(true); return; }
      this.querySelectorAll('.cp-facet[open]').forEach((o) => { o.open = false; });
    }

    onPop() {
      this.closeSheet(false);
      this.render(this.cleanParams(location.search), { push: false });
    }

    onMq() {
      if (MQ.matches) this.closeSheet(true);
      this.querySelectorAll('.cp-facet[open]').forEach((o) => { o.open = false; });
    }

    /* ---------- mobile sheet ---------- */

    openSheet() {
      if (MQ.matches) return;
      const facets = this.querySelector('[data-cp-region="facets"]');
      const count = this.querySelector('[data-cp-region="apply-count"]');
      this.snapshot = { facets: facets.innerHTML, count: count ? count.innerHTML : '' };
      this.sheet.classList.add('is-open');
      document.documentElement.classList.add('cp-lock');
      this.querySelector('[data-cp-open]').setAttribute('aria-expanded', 'true');
      const close = this.sheet.querySelector('[data-cp-close]');
      if (close) close.focus();
    }

    closeSheet(revert) {
      if (!this.sheet || !this.sheet.classList.contains('is-open')) return;
      if (this.controller) this.controller.abort();
      if (revert && this.snapshot) {
        this.querySelector('[data-cp-region="facets"]').innerHTML = this.snapshot.facets;
        const count = this.querySelector('[data-cp-region="apply-count"]');
        if (count) count.innerHTML = this.snapshot.count;
      }
      this.snapshot = null;
      this.sheet.classList.remove('is-open');
      document.documentElement.classList.remove('cp-lock');
      const open = this.querySelector('[data-cp-open]');
      open.setAttribute('aria-expanded', 'false');
      open.focus({ preventScroll: true });
    }
  }

  customElements.define('cp-collection', CpCollection);

  /*
   * R19 swatches on colour-variant cards (delegated from document, so cards added by load-more / infinite scroll and
   * filter re-renders work without re-binding):
   *   hover (fine pointer) on a swatch previews that colour on the card; leaving the card restores the selected colour
   *   click / tap on a swatch selects it (ring, swap) without navigating; the image and title open the shown colour
   *   keyboard: the selected swatch is the row's tab stop; arrows / Home / End move the selection; Enter opens it
   * The image is swapped only after the new one is decoded (no flicker); the frame and price line keep their size.
   */
  // R20 P15: the mobile side-image header sizes its image to the first screen; it needs its own distance from the page
  // top (site header + breadcrumb), which CSS can't know
  function heroTop() {
    const hero = document.querySelector('.cp-hero--image');
    if (hero) document.documentElement.style.setProperty('--cp-hero-top', `${Math.round(hero.getBoundingClientRect().top + window.scrollY)}px`);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', heroTop); else heroTop();
  window.addEventListener('load', heroTop);
  window.addEventListener('resize', heroTop);

  const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const SW = '[data-cp-sw]';
  const decoded = new Map();   // src → Promise (preload once per image)

  const swData = (a) => ({
    colour: a.dataset.cpSwColour, variant: a.dataset.cpSwVariant, href: a.dataset.cpSwHref,
    src: a.dataset.cpSwSrc, srcset: a.dataset.cpSwSrcset, sale: a.dataset.cpSwSale === '1', price: a.dataset.cpSwPrice,
  });

  function preload(card, d) {
    if (!d.src) return Promise.resolve();
    if (!decoded.has(d.src)) {
      const cur = card.querySelector('.cp-card__img');
      const im = new Image();
      if (cur && cur.sizes) im.sizes = cur.sizes;
      if (d.srcset) im.srcset = d.srcset;
      im.src = d.src;
      decoded.set(d.src, (im.decode ? im.decode() : Promise.resolve()).catch(() => {}));
    }
    return decoded.get(d.src);
  }

  function show(card, a) {
    const d = swData(a);
    if (!card.dataset.cpShown) card.dataset.cpShown = card.dataset.cpVariant;   // the card's own colour, server-rendered
    if (card.dataset.cpShown === d.variant) return;
    card.dataset.cpShown = d.variant;
    const token = (card._cpToken = (card._cpToken || 0) + 1);
    // text, price and links switch at once; the image follows when decoded
    card.querySelectorAll('[data-cp-swap="href"]').forEach((l) => { l.href = d.href; });
    const label = card.querySelector('[data-cp-swap="colour"]');
    if (label) label.textContent = d.colour;
    const price = card.querySelector('[data-cp-swap="price"]');
    if (price && d.price) price.innerHTML = d.price;
    card.classList.toggle('cp-card--sale', d.sale);
    preload(card, d).then(() => {
      if (card._cpToken !== token) return;   // a newer swap won
      const img = card.querySelector('.cp-card__img');
      if (!img || !d.src) return;
      img.loading = 'eager';
      if (d.srcset) img.srcset = d.srcset;
      img.src = d.src;
      img.alt = `${card.dataset.cpName || ''} – ${d.colour}`;
    });
  }

  function select(card, a, focus) {
    card.querySelectorAll(SW).forEach((s) => {
      const on = s === a;
      s.classList.toggle('is-current', on);
      s.tabIndex = on ? 0 : -1;
      if (on) s.setAttribute('aria-current', 'true'); else s.removeAttribute('aria-current');
    });
    show(card, a);
    card.dataset.cpColour = a.dataset.cpSwColour;
    card.dataset.cpVariant = a.dataset.cpSwVariant;
    if (focus) a.focus();
  }

  const selected = (card) => card.querySelector(`${SW}.is-current`) || card.querySelector(SW);
  const visible = (card) => [...card.querySelectorAll(SW)].filter((s) => s.offsetParent !== null);

  /*
   * R18 P11: fit the swatch row to the card. Show every swatch if they fit; otherwise as many as fit beside "+N" (at
   * least 4), "+N" counting the rest. The selected swatch always stays visible. Hidden swatches use the hidden
   * attribute, so visible() (keyboard) skips them. Re-fits on resize (ResizeObserver) and for rows added later.
   */
  const MORE_W = 22;     // room for "+N" (12px text, up to "+12")
  const MIN_SHOWN = 4;
  function fitRow(ul) {
    const lis = [...ul.querySelectorAll(':scope > .cp-sw__li')];
    const more = ul.querySelector(':scope > .cp-sw__more--lg');
    ul.classList.add('cp-sw--fit');
    const cs = getComputedStyle(ul);
    const size = parseFloat(cs.getPropertyValue('--cp-sw')) || 32;
    const gap = parseFloat(cs.columnGap) || 6;
    const w = ul.clientWidth;
    if (!w) return;
    const all = Math.floor((w + gap) / (size + gap));
    let shown = lis.length;
    if (lis.length > all) shown = Math.max(MIN_SHOWN, Math.floor((w - MORE_W) / (size + gap)));
    shown = Math.min(shown, lis.length);
    const sel = lis.findIndex((li) => li.querySelector('.is-current'));
    lis.forEach((li, k) => { li.hidden = k >= shown; });
    if (sel >= shown) { lis[shown - 1].hidden = true; lis[sel].hidden = false; }
    const rest = lis.length - shown;
    if (more) {
      more.hidden = rest <= 0;
      const a = more.querySelector('a');
      if (a && rest > 0) a.textContent = `+${rest}`;
    }
  }
  const ro = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((en) => fitRow(en.target))) : null;
  const seen = new WeakSet();
  function fitAll(root) {
    (root || document).querySelectorAll('ul.cp-sw').forEach((ul) => {
      if (seen.has(ul)) return;
      seen.add(ul);
      fitRow(ul);
      if (ro) ro.observe(ul);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => fitAll());
  else fitAll();
  new MutationObserver((muts) => {
    if (muts.some((m) => m.addedNodes.length)) fitAll();
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse' || !HOVER.matches) return;
    const a = e.target.closest(SW);
    const card = a && a.closest('[data-cp-card]');
    if (card) show(card, a);
  });
  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const card = e.target.closest('[data-cp-card]');
    if (!card || (e.relatedTarget && card.contains(e.relatedTarget))) return;
    const a = selected(card);
    if (a) show(card, a);
  });
  document.addEventListener('click', (e) => {
    const a = e.target.closest(SW);
    const card = a && a.closest('[data-cp-card]');
    if (!card) return;
    e.preventDefault();
    select(card, a, false);
  });
  document.addEventListener('keydown', (e) => {
    const a = e.target.closest && e.target.closest(SW);
    const card = a && a.closest('[data-cp-card]');
    if (!card) return;
    if (e.key === 'Enter') { e.preventDefault(); window.location.href = a.dataset.cpSwHref || a.href; return; }
    if (e.key === ' ') { e.preventDefault(); select(card, a, false); return; }
    const list = visible(card);
    const i = list.indexOf(a);
    let j = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % list.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + list.length) % list.length;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = list.length - 1;
    if (j == null || i < 0) return;
    e.preventDefault();
    select(card, list[j], true);
  });
})();

/* R42 (R38b): switching language kept the filters. Filter values are per language (Large ≠ Grand), so EN → FR with a size filter
 * landed on "0 résultats". Shopify's /localization redirect takes the query from both the form's return_to (filled with the full
 * URL) and the request's Referer, so on collection pages, in the capture phase before theme.js's LocalizationForm submits, the
 * language forms' return_to and the page URL (replaceState → Referer) become the path only: view / preview_theme_id kept;
 * filters, sort and page dropped. Country forms keep their query (values don't change with the market). */
(() => {
  const KEEP = ['view', 'preview_theme_id'];
  const toPath = (e) => {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    const item = e.target.closest && e.target.closest('.clOtp');           // a language in the list; opening the list changes nothing
    const form = item && item.closest('form[action$="/localization"]');
    if (!form || !form.querySelector('input[name="locale_code"]')) return;
    const input = form.querySelector('input[name="return_to"]');
    if (!input) return;
    const cur = new URLSearchParams(location.search);
    const p = new URLSearchParams();
    KEEP.forEach((k) => { if (cur.has(k)) p.set(k, cur.get(k)); });
    const to = location.pathname + (p.toString() ? `?${p}` : '');
    input.value = to;
    if (location.pathname + location.search !== to) history.replaceState(history.state, '', to);
  };
  document.addEventListener('click', toPath, true);
  document.addEventListener('keydown', toPath, true);
})();
